import { motion } from 'framer-motion';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  ShieldAlert,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '@/services';
import type { AttendanceRecord, User } from '@/types';
import {
  cn,
  extractErrorMessage,
  formatEmployeeId,
  formatNaira,
  formatStatus,
  formatTime,
  getLateStatusColor,
} from '@/utils';

export function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [monthlyReport, setMonthlyReport] = useState<any>(null);
  const [pendingApprovals, setPendingApprovals] = useState<User[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<AttendanceRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const [statsData, monthlyData, pendingData, attendanceData] = await Promise.all([
          adminService.getStats(),
          adminService.getMonthlySummary(now.getFullYear(), now.getMonth() + 1),
          adminService.getPendingApprovals(),
          adminService.getAllAttendance({
            startDate: now.toISOString().split('T')[0],
            endDate: now.toISOString().split('T')[0],
            limit: 100,
          }),
        ]);

        setStats(statsData);
        setMonthlyReport(monthlyData);
        setPendingApprovals(pendingData.users);
        setTodayAttendance(attendanceData.records);
      } catch (error: unknown) {
        setError(extractErrorMessage(error, 'Failed to load dashboard data'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <div className="w-12 h-12 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin" />
        <p className="text-slate-500 font-medium animate-pulse">Loading administrative data...</p>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Staff',
      value: stats?.total || 0,
      icon: Users,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
      trend: 'Across all depts',
    },
    {
      label: 'Active Now',
      value: stats?.active || 0,
      icon: CheckCircle2,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      trend: 'Verified accounts',
    },
    {
      label: 'Pending Review',
      value: pendingApprovals.length,
      icon: UserPlus,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
      trend: 'Needs action',
      onClick: () => navigate('/admin/staff?status=pending_approval'),
    },
    {
      label: 'Inactive',
      value: (stats?.suspended || 0) + (stats?.deactivated || 0),
      icon: ShieldAlert,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10',
      trend: 'Suspended/Off',
    },
  ];

  const monthlyStats = [
    {
      label: 'Present Today',
      value: todayAttendance.length,
      total: stats?.active || 0,
      icon: Calendar,
      color: 'text-indigo-500',
      bg: 'bg-indigo-500/10',
    },
    {
      label: 'Late Today',
      value: todayAttendance.filter((a) => ['late', 'very_late'].includes(a.lateCategory)).length,
      icon: Clock,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
    },
    {
      label: 'Punctuality',
      value: `${(monthlyReport?.punctuality?.punctualityRate || 0).toFixed(1)}%`,
      icon: TrendingUp,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
    },
    {
      label: 'Total Deductions',
      value: formatNaira(monthlyReport?.deductions?.total || 0),
      icon: Wallet,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Admin Overview
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            Review and manage staff attendance records, overrides, and flags.
          </p>
        </motion.div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-rose-500" />
          <p className="text-xs text-rose-700 dark:text-rose-300 font-bold">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            onClick={stat.onClick}
            className={cn(
              'p-4 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm transition-all duration-300',
              stat.onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : '',
            )}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={cn('p-3 rounded-xl', stat.bg)}>
                <stat.icon className={cn('w-5 h-5', stat.color)} />
              </div>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-neolab-green/5 dark:bg-white/5 text-xs font-black text-slate-400 uppercase tracking-tighter">
                {stat.trend}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">
                {stat.label}
              </p>
              <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                {stat.value}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {monthlyStats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.1 }}
            className="p-4 rounded-xl bg-gray-100 dark:bg-neolab-forest/70 text-gray-900 dark:text-white shadow-xl relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -mr-10 -mt-10 transition-transform group-hover:scale-150 duration-500" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div
                  className={cn('p-2 rounded-xl bg-white/10', stat.color.replace('text-', 'text-'))}
                >
                  <stat.icon className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white">
                  {stat.label}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-black tracking-tight">{stat.value}</p>
                {stat.total && (
                  <span className="text-xs font-bold text-gray-900 dark:text-white">
                    / {stat.total}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm overflow-hidden"
        >
          <div className="px-4 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-amber-500" />
              Pending Approvals
            </h2>
            <button
              type="button"
              onClick={() => navigate('/admin/staff?status=pending_approval')}
              className="text-xs font-bold text-neolab-green hover:underline"
            >
              View All
            </button>
          </div>
          <div className="p-2">
            {pendingApprovals.length === 0 ? (
              <div className="py-8 text-center text-slate-400 font-medium text-sm">
                No pending approvals
              </div>
            ) : (
              <div className="space-y-1">
                {pendingApprovals.slice(0, 5).map((user) => (
                  <button
                    type="button"
                    key={user._id}
                    className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-neolab-green/5 dark:hover:bg-white/5 transition-colors cursor-pointer group text-left"
                    onClick={() => navigate(`/admin/staff/${user._id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/admin/staff/${user._id}`);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-neolab-green/10 dark:bg-white/10 flex items-center justify-center font-bold text-slate-500 text-xs">
                        {user.profile.firstName[0]}
                        {user.profile.lastName[0]}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-900 dark:text-white">
                          {user.profile.firstName} {user.profile.lastName}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                          {user.profile.department} • {formatEmployeeId(user.employeeId)}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-neolab-green transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm overflow-hidden"
        >
          <div className="px-4 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <Clock className="w-4 h-4 text-neolab-green" />
              Today's Activity
            </h2>
            <button
              type="button"
              onClick={() => navigate('/admin/attendance')}
              className="text-xs font-bold text-neolab-green hover:underline"
            >
              View All
            </button>
          </div>
          <div className="p-2">
            {todayAttendance.length === 0 ? (
              <div className="py-8 text-center text-slate-400 font-medium text-sm">
                No activity recorded yet
              </div>
            ) : (
              <div className="space-y-1">
                {todayAttendance.slice(0, 5).map((record) => {
                  const formattedClockInTimestamp = record.clockIn?.timestamp
                    ? formatTime(record.clockIn.timestamp)
                    : '--:--';
                  const user = typeof record.userId === 'object' ? record.userId : null;
                  const statusColor = getLateStatusColor(record.lateCategory);
                  return (
                    <div
                      key={record._id}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-neolab-green/5 dark:hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-neolab-green/10 dark:bg-white/10 flex items-center justify-center font-bold text-slate-500 text-xs">
                          {user?.profile.firstName[0]}
                          {user?.profile.lastName[0]}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900 dark:text-white">
                            {user
                              ? `${user.profile.firstName} ${user.profile.lastName}`
                              : 'Unknown'}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                            Clocked in at {formattedClockInTimestamp}
                          </p>
                        </div>
                      </div>
                      <div
                        className={cn(
                          'px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest',
                          statusColor === 'green' && 'bg-emerald-500/10 text-emerald-600',
                          statusColor === 'orange' && 'bg-amber-500/10 text-amber-600',
                          statusColor === 'red' && 'bg-rose-500/10 text-rose-600',
                        )}
                      >
                        {formatStatus(record.lateCategory)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default AdminDashboard;
